import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { logPepAppModalShown, logPepAppModalButtonClick } from '../services/analytics'

const PEPAPP_DEEPLINK = 'launchpepapp://assistant?type=premium&assetId=Anniversary'

interface PepAppPremiumModalProps {
  open: boolean
  onClose: () => void
}

export default function PepAppPremiumModal({ open, onClose }: PepAppPremiumModalProps) {
  // Prevent body scroll when modal is open (mobile) + log modal shown
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'
      logPepAppModalShown()
    }
    return () => {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
  }, [open])

  const handleOpenPepApp = () => {
    logPepAppModalButtonClick('open_app')
    window.location.href = PEPAPP_DEEPLINK
    onClose()
  }

  const handleClose = (action: 'dismiss' | 'close') => () => {
    logPepAppModalButtonClick(action)
    onClose()
  }

  if (!open) return null

  // Portal to document.body so modal is above Layout (mascot, header, etc.)
  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pepapp-modal-title"
    >
      {/* Backdrop - tap to close on mobile */}
      <button
        type="button"
        onClick={handleClose('close')}
        className="absolute inset-0 bg-ink/50 backdrop-blur-sm"
        aria-label="Kapat"
      />

      {/* Modal - bottom sheet on mobile, centered on desktop */}
      <div
        className="relative w-full max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl animate-slide-up flex flex-col max-h-[90vh] sm:max-h-[85vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pt-4 pb-6 px-4 sm:p-6 flex flex-col" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
          {/* Close - large touch target */}
          <div className="flex justify-end -mt-1 mb-2">
            <button
              type="button"
              onClick={handleClose('close')}
              className="p-3 -mr-2 text-ink/50 hover:text-ink active:bg-rose-50 rounded-full transition-colors touch-manipulation"
              aria-label="Kapat"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex flex-col items-center text-center pb-2">
            <img
              src="/pepapp-logo.png"
              alt="Pepapp"
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl object-contain mb-4 shadow-lg"
            />
            <h2
              id="pepapp-modal-title"
              className="font-display text-xl sm:text-2xl font-semibold text-ink mb-2"
            >
              7 Gün Ücretsiz Pepapp Premium
            </h2>
            <p className="text-ink/70 text-sm sm:text-base font-body mb-6 max-w-xs">
              İlişkini güçlendirmek için Pepapp Premium&apos;u ücretsiz dene. Yıldönümü özel deneyimini keşfet.
            </p>
          </div>

          {/* CTA - primary: open app */}
          <button
            type="button"
            onClick={handleOpenPepApp}
            className="btn-primary w-full py-4 text-base font-semibold flex items-center justify-center gap-2 touch-manipulation min-h-[48px]"
          >
            Hemen Başla
          </button>

          {/* Secondary: dismiss */}
          <button
            type="button"
            onClick={handleClose('dismiss')}
            className="mt-3 py-3 text-ink/60 hover:text-ink text-sm font-body touch-manipulation min-h-[44px]"
          >
            Şimdi değil
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
